import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { GuidedSearchSetting } from '@interfaces/guided-search-setting.model';
import { TrackByUtilities } from '@utilities/trackByFn.utilities';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';
import { GuidedSearchService } from '@services/guided-search/guided-search.service';
import { RouteUtilities } from '@utilities/route.utilities';
import { Router } from '@angular/router';
import { MatDialogRef } from '@angular/material/dialog';
import { GuidedSearchDialogComponent } from '../guided-search-dialog/guided-search-dialog.component';
import { GatedEntryService } from '@services/gated-entry/gated-entry.service';
import { GatedEntryStatus } from '@interfaces/gated-entry-status.interface';

@Component({
  selector: 'app-guided-search-browse-by',
  templateUrl: './guided-search-browse-by.component.html',
})
export class GuidedSearchBrowseByComponent implements OnInit, OnDestroy {
  @Input() public authStatus: boolean = false;
  @Output() public triggerRtsWelcomeOverlay: EventEmitter<void> =
    new EventEmitter();

  public categories: GuidedSearchSetting[];
  public isLoading: boolean = true;
  public tileCount: number = 0;
  public stopIndex: number;
  public trackByName: any = TrackByUtilities.getTrackByFn('name');
  public showAllCategories: boolean = false;

  private destroy = new Subject();
  private modalRef: MatDialogRef<GuidedSearchDialogComponent, any>;
  private gatedEntryStatusSubscription: Subscription;

  constructor(
    private guidedSearchService: GuidedSearchService,
    private changeDetector: ChangeDetectorRef,
    private routeUtilities: RouteUtilities,
    private router: Router,
    private gatedEntryService: GatedEntryService
  ) {}

  ngOnInit() {
    this.subscribeToCategories();
  }

  ngOnDestroy(): void {
    this.destroySubscriptions();
    this.removeGuidedSearchParam();
  }

  private subscribeToCategories(): void {
    this.getCategories().subscribe((settings) => {
      this.categories = settings;
      this.countTiles(settings);
      this.isLoading = false;
      this.openModalOnAppLoad();
      // Workaround for change detection not seeing new tiles
      setTimeout(() => this.changeDetector.detectChanges());
    });
  }

  private openModalOnAppLoad(): void {
    const guidedSearchParam =
      this.routeUtilities.getParamFromUrl('guided_search');
    if (guidedSearchParam) {
      const option = this.categories.find(
        (category: GuidedSearchSetting) => category?.name === guidedSearchParam
      );
      if (option) {
        this.handleOption(option);
      } else {
        this.triggerRtsWelcomeOverlay.emit();
      }
    }
  }

  private handleOption(option: GuidedSearchSetting): void {
    const gateEntryOpen = this.gatedEntryService.status.getValue().isOpened;
    if (gateEntryOpen) {
      this.listenToGatedEntryClose(option);
      return;
    }
    this.openSelectedGuidedSearch(option);
  }

  private listenToGatedEntryClose(option: GuidedSearchSetting): void {
    if (!this.gatedEntryStatusSubscription) {
      this.gatedEntryStatusSubscription = this.gatedEntryService.status
        .pipe(
          takeUntil(this.destroy),
          filter((status: GatedEntryStatus) => status.isClosed),
          take(1)
        )
        .subscribe(() => this.openSelectedGuidedSearch(option));
    }
  }

  private openSelectedGuidedSearch(option: GuidedSearchSetting): void {
    this.modalRef = this.guidedSearchService.selectGuidedSearchOption(option);
    this.listenToModalClose();
    this.removeGuidedSearchParam();
  }

  private listenToModalClose(): void {
    this.modalRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => this.triggerRtsWelcomeOverlay.emit());
  }

  private removeGuidedSearchParam(): void {
    const params = this.routeUtilities.getParamsFromUrl();
    if (params.guided_search) {
      delete params.guided_search;
      this.router.navigate([], {
        queryParams: params,
        replaceUrl: true,
      });
    }
  }

  private getCategories(): Observable<GuidedSearchSetting[]> {
    return this.guidedSearchService.guidedSearchOptions.pipe(
      takeUntil(this.destroy),
      debounceTime(750),
      map((settings) => settings || [])
    );
  }

  private countTiles(categories: GuidedSearchSetting[]): void {
    this.tileCount = categories.length;
    this.stopIndex = 6;
    this.showAllCategories = this.tileCount > 7;
  }

  private destroySubscriptions(): void {
    this.destroy.next(undefined);
    this.destroy.unsubscribe();
  }
}
